import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import QRCode from 'qrcode';
import api from '../api';
import ProductGrid from '../components/pos/ProductGrid';
import Cart from '../components/pos/Cart';
import CustomerSearch from '../components/pos/CustomerSearch';
import ShiftStats from '../components/pos/ShiftStats';
import VolumeModal from '../components/pos/VolumeModal';
import PaymentModal from '../components/pos/PaymentModal';
import ReceiptModal from '../components/pos/ReceiptModal';

// Дата-час за Києвом у форматі DD.MM.YYYY HH:MM
function kyivDateTime() {
  const parts = new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'Europe/Kyiv', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const g = t => (parts.find(p => p.type === t) || {}).value || '';
  return `${g('day')}.${g('month')}.${g('year')} ${g('hour')}:${g('minute')}`;
}

// HTML наклейки 58×40 (формат як у старій 1С: лого, назва, об'єм, адреса, дата)
// logo — data URL логотипу магазину; якщо порожньо, малюємо стандартний значок Draft Beer.
function buildLabelHtml(name, volStr, address, logo) {
  const esc = s => String(s || '').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
  const leaf = '<svg viewBox="0 0 24 24" width="16" height="16" style="vertical-align:middle">'
    + '<circle cx="12" cy="12" r="11" fill="none" stroke="#000" stroke-width="0.7" stroke-dasharray="2 1.6"/>'
    + '<path d="M12 5.5C9 8 7.5 10.5 7.5 13.5a4.5 4.5 0 009 0c0-3-1.5-5.5-4.5-8z" fill="none" stroke="#000" stroke-width="1.1"/>'
    + '<path d="M12 8v9M12 11l2 1.3M12 11l-2 1.3M12 14l2 1.3M12 14l-2 1.3" stroke="#000" stroke-width="0.9" fill="none"/></svg>';
  const header = logo
    ? '<img src="' + esc(logo) + '" style="max-height:8mm;max-width:52mm;object-fit:contain" />'
    : leaf + '<b>Draft Beer</b>';
  // Драйвер тепер налаштований на правильний розмір 58×40 — макет заповнює всю етикетку
  // через flex space-between (лого зверху, назва/обʼєм по центру, футер знизу).
  return '<html><head><meta charset="utf-8"><style>'
    + '@page{size:58mm 40mm;margin:0}html,body{margin:0;padding:0}'
    + '*{margin:0;box-sizing:border-box;font-family:Arial,Helvetica,sans-serif;color:#000}'
    + 'body{width:58mm;height:40mm;padding:2mm 2.5mm;overflow:hidden;'
    + 'display:flex;flex-direction:column;justify-content:space-between;text-align:center}'
    + '.logo{line-height:1;padding-bottom:1mm;border-bottom:0.4mm solid #000}'
    + '.logo b{font-size:13pt;font-weight:800;margin-left:5px;vertical-align:middle;letter-spacing:0.3px}'
    + '.name{font-size:13pt;font-weight:800;line-height:1.1;'
    + 'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}'
    + '.vol{font-size:15pt;font-weight:800}'
    + '.foot{padding-top:1mm;border-top:0.4mm solid #000}'
    + '.addr{font-size:7pt;line-height:1.15;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}'
    + '.dt{font-size:7.5pt;font-weight:600;margin-top:0.4mm}'
    + '</style></head><body>'
    + '<div class="logo">' + header + '</div>'
    + '<div class="name">' + esc(name) + '</div>'
    + '<div class="vol">' + esc(volStr) + '</div>'
    + '<div class="foot"><div class="addr">' + esc(address) + '</div>'
    + '<div class="dt">' + kyivDateTime() + '</div></div>'
    + '</body></html>';
}

// HTML чека для термопринтера 48мм (тихий друк у десктоп-касі, без модалки).
// Структура повторює ReceiptModal, але версткою під вузьку стрічку (моноширинний шрифт).
function buildReceiptHtml(receipt, store, storeName, meta) {
  const esc = s => String(s == null ? '' : s).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
  const money = v => parseFloat(v || 0).toFixed(2);
  const PAY = { cash: 'Готівка', card: 'Картка', mixed: 'Змішана', bonus: 'Бонуси' };
  const items = receipt?.items || [];
  const total = parseFloat(receipt?.total_amount || 0);
  const bonusUsed = parseFloat(receipt?.bonus_used || 0);
  const bonusEarned = parseFloat(receipt?.bonus_earned || 0);
  const cashGiven = parseFloat(meta?.cash_given || 0);
  const change = parseFloat(meta?.change || 0);
  const dt = receipt?.created_at
    ? new Date(receipt.created_at).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : kyivDateTime();
  const num = receipt?.id ? '#' + String(receipt.id).slice(-8).toUpperCase() : '';

  const logo = store?.logo
    ? '<img src="' + esc(store.logo) + '" style="max-height:9mm;max-width:40mm;object-fit:contain" />'
    : '<b style="font-size:12pt">Draft Beer</b>';

  const head = [storeName, store?.legal_name, store?.tax_id ? 'ЄДРПОУ/ІПН: ' + store.tax_id : '', store?.address, store?.phone]
    .filter(Boolean)
    .map((l, i) => '<div class="' + (i === 0 ? 'store' : 'sub') + '">' + esc(l) + '</div>').join('');

  const itemsHtml = items.map(it =>
    '<div class="it"><div class="nm">' + esc(it.product_name) + '</div>'
    + '<div class="row"><span>' + parseFloat(it.quantity).toFixed(3) + ' × ' + parseFloat(it.price).toFixed(2) + '</span>'
    + '<span class="b">' + parseFloat(it.total).toFixed(2) + '</span></div></div>'
  ).join('');

  const rows = [];
  if (bonusUsed > 0) rows.push('<div class="row"><span>Бонуси списано</span><span>-' + money(bonusUsed) + '</span></div>');
  rows.push('<div class="row tot"><span>РАЗОМ</span><span>' + money(total) + ' грн</span></div>');
  rows.push('<div class="row"><span>Оплата</span><span>' + (PAY[receipt?.payment_method] || esc(receipt?.payment_method)) + '</span></div>');
  if (cashGiven > 0 && receipt?.payment_method === 'cash') {
    rows.push('<div class="row"><span>Отримано</span><span>' + money(cashGiven) + '</span></div>');
    rows.push('<div class="row"><span>Решта</span><span>' + money(change) + '</span></div>');
  }
  if (bonusEarned > 0) rows.push('<div class="row"><span>Нараховано бонусів</span><span>+' + money(bonusEarned) + '</span></div>');

  return '<html><head><meta charset="utf-8"><style>'
    + '@page{margin:0}html,body{margin:0;padding:0}'
    + '*{box-sizing:border-box;font-family:"Courier New",monospace;color:#000}'
    + 'body{width:48mm;padding:1mm 1.5mm;font-size:9pt;line-height:1.25}'
    + '.store{font-size:12pt;font-weight:bold;text-align:center}'
    + '.sub{font-size:8pt;text-align:center;line-height:1.2}'
    + '.dt{font-size:8pt;text-align:center}.num{font-size:8pt;text-align:center;font-weight:bold}'
    + 'hr{border:none;border-top:1px dashed #000;margin:1.5mm 0}'
    + '.it{margin-bottom:1mm}.nm{font-weight:bold;font-size:9pt}'
    + '.row{display:flex;justify-content:space-between;font-size:9pt}.b{font-weight:bold}'
    + '.tot{font-size:12pt;font-weight:bold;margin:1mm 0}'
    + '.foot{text-align:center;font-size:8pt;margin-top:1.5mm}'
    + '</style></head><body>'
    + '<div style="text-align:center;margin-bottom:1mm">' + logo + '</div>'
    + head
    + '<div class="sub">Товарний чек</div>'
    + '<div class="dt">' + dt + '</div>'
    + (num ? '<div class="num">' + num + '</div>' : '')
    + '<hr>' + itemsHtml + '<hr>'
    + rows.join('')
    + '<hr><div class="foot">' + esc(store?.receipt_footer || 'Дякуємо за покупку!') + '</div>'
    + '</body></html>';
}

// ── Побудова тіла чека реалізації ПРРО iNNOVATE з продажу CRM (у веб-шарі, щоб правити без .exe).
// codeUnit/vatGroup — точні значення цього ПРРО: шт=9/л=138/кг=301; акциз=Б, без ПДВ=В.
const PRRO_UNIT = { piece: { codeUnit: 9, unitName: 'шт' }, liter: { codeUnit: 138, unitName: 'л' }, gram: { codeUnit: 301, unitName: 'кг' } };
const r2p = n => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
function prroVat(it) { const g = (it.tax_group || '').trim(); if (g === 'Б' || g === 'В') return g; return it.is_excise ? 'Б' : 'В'; }
function buildRealizeBody(sale, opts = {}) {
  const items = sale.items || [];
  const base = items.map(it => ({ it, amount0: r2p(Number(it.price) * Number(it.quantity)) }));
  const subtotal = r2p(base.reduce((s, x) => s + x.amount0, 0));
  const charged = r2p(Number(sale.total_amount));
  let discountTotal = r2p(subtotal - charged);
  if (discountTotal < 0) discountTotal = 0;

  const body = [];
  let distributed = 0;
  base.forEach((x, idx) => {
    const last = idx === base.length - 1;
    let discountSum = subtotal > 0 ? r2p(discountTotal * (x.amount0 / subtotal)) : 0;
    if (last) discountSum = r2p(discountTotal - distributed);
    distributed = r2p(distributed + discountSum);
    const u = PRRO_UNIT[x.it.unit] || PRRO_UNIT.piece;
    // ВАЖЛИВО: amount = БРУТТО (ціна×кількість); знижка окремо в discountSum.
    // ПРРО сам рахує нетто = amount − discountSum (інакше DocumentValidationError).
    const line = { type: '1', name: x.it.product_name, price: r2p(x.it.price), quantity: Number(x.it.quantity),
      discountSum, amount: r2p(x.amount0), codeUnit: u.codeUnit, unitName: u.unitName, number: idx + 1, vatGroup: prroVat(x.it) };
    if (x.it.uktzed) line.UKTZED = String(x.it.uktzed);
    body.push(line);
  });

  let n = body.length + 1;
  const c = opts.card;
  const cardLine = (sum) => ({ type: '2', sum: r2p(sum), returnSum: 0, typePay: 3, name: 'КАРТКА', number: n++,
    cards: [{ sumPay: r2p(sum), sumCashBack: 0, comission: 0, acquirer: (c && c.acquirer) || 'ПриватБанк', posId: (c && c.posId) || '',
      ePayType: (c && c.pan) || '', paySystem: (c && c.paymentSystem) || '', authCode: (c && c.approvalCode) || '',
      transactionId: (c && (c.invoice || c.transactionId)) || '', payTypeName: 'ОПЛАТА', signNeeded: 0 }] });
  const cashLine = (sum) => ({ type: '2', sum: r2p(sum), returnSum: 0, name: 'ГОТІВКА', typePay: 0, number: n++ });

  if (sale.payment_method === 'mixed') {
    const cashPart = r2p(Math.min(Number(opts.cashGiven || 0), charged));
    const cardPart = r2p(charged - cashPart);
    if (cashPart > 0) body.push(cashLine(cashPart));
    if (cardPart > 0) body.push(cardLine(cardPart));
    if (cashPart <= 0 && cardPart <= 0) body.push(cashLine(charged));
  } else if (sale.payment_method === 'card' && c) {
    body.push(cardLine(charged));
  } else {
    body.push(cashLine(charged));
  }
  return { docSubType: '0', sum: charged, roundDiscount: 0, body, cashierName: opts.cashierName || 'Касир' };
}

// Офіційний ФІСКАЛЬНИЙ чек ПРРО iNNOVATE — гарний рендер зі СТРУКТУРОВАНИХ даних відповіді
// /innovate/printreceipt (body, taxes, sum, QR, fiscalNumber…). Містить усі обов'язкові
// реквізити + читабельне оформлення (центрування, жирні заголовки/підсумок, УКТ ЗЕД під позицією).
function buildFiscalReceiptHtml(prro, qrDataUrl) {
  const esc = s => String(s == null ? '' : s).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
  const money = v => (Math.round((Number(v) + Number.EPSILON) * 100) / 100).toFixed(2);
  const qrUrl = (prro.QR || '').trim();
  // QR: локальна data-URL (без інтернету — друк не підвисає) або зовнішній фолбек.
  const qrSrc = qrDataUrl || (qrUrl ? 'https://api.qrserver.com/v1/create-qr-code/?size=170x170&qzone=1&data=' + encodeURIComponent(qrUrl) : '');
  const body = Array.isArray(prro.body) ? prro.body : [];

  // Якщо структури немає (старий формат) — падаємо на друк готового тексту.
  if (!body.length) {
    const text = String(prro.text || '').replace(/@@QR@@.*?(\r?\n|$)/g, '');
    return '<html><head><meta charset="utf-8"><style>@page{margin:0}body{width:48mm;margin:0;padding:1mm 1.5mm}'
      + 'pre{font-family:"Courier New",monospace;color:#000;font-size:10pt;font-weight:600;line-height:1.25;white-space:pre-wrap;word-break:break-word;margin:0}'
      + '.qr{text-align:center;margin:1.5mm 0}</style></head><body><pre>' + esc(text) + '</pre>'
      + (qrSrc ? '<div class="qr"><img src="' + qrSrc + '" style="width:34mm;height:34mm"/></div>' : '')
      + '</body></html>';
  }

  const items = body.filter(b => String(b.type) === '1');
  const pays = body.filter(b => String(b.type) === '2');
  const taxes = Array.isArray(prro.taxes) ? prro.taxes : [];

  const itemsHtml = items.map(it => {
    const qty = Number(it.quantity);
    const qtyStr = (Math.abs(qty - Math.round(qty)) < 1e-9) ? String(Math.round(qty)) : qty.toFixed(3);
    let h = '<div class="it"><div class="nm">' + esc(it.name) + '</div>'
      + '<div class="row"><span>' + qtyStr + ' × ' + money(it.price) + (it.unitName ? ' ' + esc(it.unitName) : '') + '</span>'
      + '<span class="b">' + money(it.amount) + (it.vatGroup ? ' ' + esc(it.vatGroup) : '') + '</span></div>';
    if (it.UKTZED) h += '<div class="sm">УКТ ЗЕД: ' + esc(it.UKTZED) + '</div>';
    if (it.excises && it.excises.length) h += '<div class="sm">Акцизна марка: ' + esc(it.excises.join(', ')) + '</div>';
    return h + '</div>';
  }).join('');

  const paysHtml = pays.map(p => {
    const nm = p.name || (String(p.typePay) === '3' ? 'КАРТКА' : 'ГОТІВКА');
    return '<div class="row"><span>' + esc(nm) + '</span><span class="b">' + money(p.sum) + ' грн</span></div>';
  }).join('');

  const taxesHtml = taxes.map(t => {
    const label = esc(t.name || '') + (t.letters ? ' ' + esc(t.letters) : '') + (t.percent != null ? ' ' + t.percent + '%' : '');
    return '<div class="row sm"><span>' + label + '</span><span>' + money(t.sum) + '</span></div>';
  }).join('');

  const online = prro.offline ? 'ОФЛАЙН' : 'ОНЛАЙН';
  const head = [
    prro.goName ? '<div class="store">' + esc(prro.goName) + '</div>' : '',
    prro.orgName ? '<div class="sub">' + esc(prro.orgName) + '</div>' : '',
    prro.goAddress ? '<div class="sub">' + esc(prro.goAddress) + '</div>' : '',
    prro.IPN ? '<div class="sub">ПН: ' + esc(prro.IPN) + '</div>' : '',
    (prro.EDRPOU ? '<div class="sub">ІД: ' + esc(prro.EDRPOU) + '</div>' : ''),
    prro.cashierName ? '<div class="sub">Оператор: ' + esc(prro.cashierName) + '</div>' : '',
  ].join('');

  const qrImg = qrSrc
    ? '<div class="qr"><img src="' + qrSrc + '" style="width:34mm;height:34mm" /></div>'
    : '';

  return '<html><head><meta charset="utf-8"><style>'
    + '@page{margin:0}html,body{margin:0;padding:0}'
    + '*{box-sizing:border-box;font-family:"Courier New",monospace;color:#000}'
    + 'body{width:48mm;padding:1mm 1.5mm;font-size:10pt;line-height:1.3;font-weight:600}'
    + '.store{font-size:15pt;font-weight:800;text-align:center;line-height:1.1;margin-bottom:1mm}'
    + '.sub{font-size:8.5pt;text-align:center;line-height:1.2;font-weight:600}'
    + 'hr{border:none;border-top:1px dashed #000;margin:1.5mm 0}'
    + '.it{margin-bottom:1mm}.nm{font-weight:800;font-size:10pt}'
    + '.row{display:flex;justify-content:space-between;gap:4px}'
    + '.sm{font-size:8pt;font-weight:600;color:#000}'
    + '.b{font-weight:800}'
    + '.tot{font-size:14pt;font-weight:800;margin:1mm 0}'
    + '.foot{text-align:center;font-size:8.5pt;font-weight:600;line-height:1.3}'
    + '.fisc{text-align:center;font-size:11pt;font-weight:800;margin:1mm 0}'
    + '.qr{text-align:center;margin:1.5mm 0}'
    + '</style></head><body>'
    + head
    + '<hr>' + itemsHtml + '<hr>'
    + paysHtml
    + '<hr>'
    + '<div class="row tot"><span>СУМА</span><span>' + money(prro.sum) + '</span></div>'
    + taxesHtml
    + '<hr>'
    + '<div class="foot">Дякуємо за покупку!</div>'
    + '<div class="foot">ЧЕК ' + esc(prro.fiscalNumber || '') + ' &nbsp; ' + online + '</div>'
    + '<div class="foot">' + esc(prro.date || '') + '</div>'
    + '<div class="foot">ФН ПРРО ' + esc(prro.cashRegisterNum || '') + ' &nbsp; №' + esc(prro.receiptNumber != null ? prro.receiptNumber : '') + '</div>'
    + '<div class="fisc">ФІСКАЛЬНИЙ ЧЕК</div>'
    + '<div class="foot">iNNOVATE!</div>'
    + qrImg
    + '</body></html>';
}

export default function POSPage() {
  const { id: storeId } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(null);
  const [prroShift, setPrroShift] = useState(null);  // реальний статус зміни з ПРРО (десктоп-каса)
  const [storeName, setStoreName] = useState('');
  const [storeInfo, setStoreInfo] = useState(null);
  const [todaySales, setTodaySales] = useState({ sales_count:0, total:'0.00' });
  const [activePromos, setActivePromos] = useState([]);
  const [volumeModal, setVolumeModal] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [receiptMeta, setReceiptMeta] = useState({ cash_given:0, change:0 });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [error, setError] = useState('');
  const [maxSpendFrac, setMaxSpendFrac] = useState(0.3);  // макс. частка списання бонусів від чека (з налаштувань)
  const [earnFrac, setEarnFrac] = useState(0.01);         // частка нарахування бонусів (з налаштувань)

  const { user } = useAuth();

  // Ваги (десктоп-каса): живий вес зі служби через window.hardware
  const isDesktop = typeof window !== 'undefined' && !!window.hardware;
  const [liveWeight, setLiveWeight] = useState(null);   // {weight, stable}

  useEffect(() => {
    if (!isDesktop || !window.hardware.onWeight) return;
    const off = window.hardware.onWeight(w => setLiveWeight(w));
    return () => { try { off && off(); } catch(e){} };
  }, [isDesktop]);

  const storeSettingsRef = React.useRef({ cap_enabled:false, cap_product:null, bag_enabled:false, bag_product:null });

  // Мультичек
  const emptyCheck = () => ({ id: Date.now(), cart:[], customer:null, bonusToUse:'0', paymentMethod:'cash', noPromo:false });
  const [checks, setChecks] = useState([emptyCheck()]);
  const [activeCheckId, setActiveCheckId] = useState(null);

  const activeCheck = checks.find(c => c.id === activeCheckId) || checks[0];
  const cart = activeCheck?.cart || [];
  const customer = activeCheck?.customer || null;
  const bonusToUse = activeCheck?.bonusToUse || '0';
  const paymentMethod = activeCheck?.paymentMethod || 'cash';
  const noPromo = activeCheck?.noPromo || false;

  const activeId = activeCheckId || checks[0]?.id;

  function updateCheck(id, patch) {
    setChecks(prev => prev.map(c => c.id === id ? {...c, ...patch} : c));
  }

  function setCart(fn) {
    const id = activeCheckId || checks[0]?.id;
    setChecks(prev => prev.map(c => c.id === id
      ? {...c, cart: typeof fn === 'function' ? fn(c.cart) : fn}
      : c
    ));
  }

  function setCustomer(val) {
    updateCheck(activeId, { customer: typeof val === 'function' ? val(customer) : val });
  }

  function setBonusToUse(val) {
    updateCheck(activeId, { bonusToUse: typeof val === 'function' ? val(bonusToUse) : val });
  }

  function setPaymentMethod(val) {
    updateCheck(activeId, { paymentMethod: val });
  }

  function setNoPromo(val) {
    updateCheck(activeId, { noPromo: val });
  }

  function addNewCheck() {
    const c = emptyCheck();
    setChecks(prev => [...prev, c]);
    setActiveCheckId(c.id);
  }

  function removeCheck(id) {
    setChecks(prev => {
      const remaining = prev.filter(c => c.id !== id);
      if (remaining.length === 0) {
        const fresh = emptyCheck();
        setActiveCheckId(fresh.id);
        return [fresh];
      }
      if (activeCheckId === id) setActiveCheckId(remaining[remaining.length - 1].id);
      return remaining;
    });
  }

  // Розкладка акцій для ВІТРИНИ кошика (та сама логіка, що в бекенді _apply_promotions).
  // buy_x_get_y — «вирізає» подарунок ОКРЕМИМ рядком 🎁 по 0.01 (видно як позицію).
  // percent/fixed — грошова знижка (рядком «🎁 назва −X»). У бекенд шлемо реальні
  // кількості (з activeCheck.cart), сервер рахує те саме — він джерело правди.
  function matchesPromo(it, promo) {
    if (it.is_free || it.is_container_line) return false;
    if (promo.scope === 'product') return it.product_id === promo.scope_id;
    if (promo.scope === 'category') return (products.find(p => p.id === it.product_id)?.category_id) === promo.scope_id;
    if (promo.scope === 'cart') return true;
    return false;
  }

  function splitPromos(items) {
    const result = items.map(i => ({ ...i }));
    const freeLines = [];
    let moneyDiscount = 0; const lines = [];
    for (const promo of activePromos) {
      const matched = result.filter(it => matchesPromo(it, promo));
      if (!matched.length) continue;
      if (promo.type === 'buy_x_get_y') {
        const g = parseFloat(promo.buy_quantity) || 0, fn = parseFloat(promo.get_quantity) || 0;
        if (g <= 0 || fn <= 0) continue;
        const matchedQty = matched.reduce((s, it) => s + it.quantity, 0);
        let freeUnits = Math.floor(matchedQty / g) * fn;
        if (freeUnits <= 0) continue;
        matched.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        const fp = matched[0];
        // подарунок ДОДАЄТЬСЯ окремим рядком, НЕ зменшує пробиту позицію.
        // обмежуємо наявним залишком (це додаткова кількість).
        const stock = parseFloat(products.find(p => p.id === fp.product_id)?.stock_quantity ?? 99999);
        const paidFp = result.filter(it => !it.is_free && it.product_id === fp.product_id)
          .reduce((s, it) => s + it.quantity, 0);
        freeUnits = Math.min(freeUnits, Math.round((stock - paidFp) * 1000) / 1000);
        if (freeUnits <= 0) continue;
        freeLines.push({
          cartKey: 'free_' + promo.id,
          product_id: fp.product_id,
          product_name: '🎁 ' + fp.product_name.replace(/ \d+(\.\d+)?л$/, ''),
          price: '0.01', unit: fp.unit,
          quantity: Math.round(freeUnits * 1000) / 1000,
          stock_quantity: 99999, is_free: true,
        });
      } else if (promo.type === 'percent_discount' || promo.type === 'fixed_discount') {
        const matchedSum = matched.reduce((s, it) => s + parseFloat(it.price) * it.quantity, 0);
        const d = promo.type === 'percent_discount'
          ? matchedSum * (parseFloat(promo.discount_value) || 0) / 100
          : Math.min(parseFloat(promo.discount_value) || 0, matchedSum);
        if (d > 0) { moneyDiscount += d; lines.push({ name: promo.name, save: Math.round(d * 100) / 100 }); }
      } else if (promo.type === 'nth_percent') {
        // «купи X — на наступні Y знижка Z%»: у кожній групі (X+Y) — Y одиниць зі знижкою
        const buyN = parseFloat(promo.buy_quantity) || 0, discN = parseFloat(promo.get_quantity) || 0;
        const pct = parseFloat(promo.discount_value) || 0, group = buyN + discN;
        if (group > 0 && discN > 0 && pct > 0) {
          const matchedQty = matched.reduce((s, it) => s + it.quantity, 0);
          const discUnits = Math.floor(matchedQty / group) * discN;
          if (discUnits > 0) {
            const unitPrice = Math.min(...matched.map(it => parseFloat(it.price)));
            const d = discUnits * unitPrice * pct / 100;
            moneyDiscount += d; lines.push({ name: promo.name, save: Math.round(d * 100) / 100 });
          }
        }
      }
    }
    const cart = [...result.filter(i => i.is_container_line || i.quantity > 0.0001), ...freeLines];
    return { cart, moneyDiscount: Math.round(moneyDiscount * 100) / 100, lines };
  }

  useEffect(() => { init(); }, [storeId]);

  // Глобальні правила бонусів (спільні для всіх точок) — % нарахування та макс. % списання.
  useEffect(() => {
    api.get('/settings/bonus')
      .then(r => {
        setMaxSpendFrac((parseFloat(r.data.max_spend_percent) || 0) / 100);
        setEarnFrac((parseFloat(r.data.earn_percent) || 0) / 100);
      })
      .catch(() => {});
  }, []);

  // Підтягувати свіжі акції при поверненні на вкладку каси (раптом змінили в адмінці)
  useEffect(() => {
    async function refreshPromos() {
      if (!storeId || document.visibilityState !== 'visible') return;
      try {
        const r = await api.get(`/promotions/stores/${storeId}/active`);
        setActivePromos(r.data);
      } catch (e) {}
      // Перечитати статус зміни з ПРРО (раптом щойно ввели пароль у Налаштуваннях пристроїв)
      if (isDesktop && window.hardware.prro && window.hardware.prro.status) {
        try {
          const st = await window.hardware.prro.status();
          if (st && st.ok) {
            setPrroShift(st);
            const cur = (await api.get(`/shifts/store/${storeId}/active`).catch(() => ({ data: null }))).data;
            if (!cur) await api.post(`/shifts/open/${storeId}`).catch(() => {});
            setShiftOpen(true);
          }
        } catch (e) {}
      }
    }
    document.addEventListener('visibilitychange', refreshPromos);
    window.addEventListener('focus', refreshPromos);
    return () => {
      document.removeEventListener('visibilitychange', refreshPromos);
      window.removeEventListener('focus', refreshPromos);
    };
  }, [storeId]);

  async function init() {
    let crmShift = (await api.get(`/shifts/store/${storeId}/active`).catch(() => ({ data: null }))).data;

    // Десктоп-каса: ДЖЕРЕЛО ПРАВДИ про зміну — реальний ПРРО (його відкриває/закриває 1С чи каса),
    // а не внутрішня CRM-зміна. Якщо ПРРО-зміна відкрита — тихо синхронізуємо CRM-зміну (внутрішній
    // запис для продажів/звітів; ДРУГУ фіскальну зміну в ПРРО це НЕ створює).
    if (isDesktop && window.hardware.prro && window.hardware.prro.status) {
      try {
        const st = await window.hardware.prro.status();
        if (st && st.ok) {
          setPrroShift(st);
          // ПРРО на звʼязку → каса працює. НЕ блокуємо, якщо фіскальна зміна закрита:
          // у iNNOVATE зміну відкриває ПЕРШИЙ чек. Тихо синхронізуємо CRM-зміну для бекенду.
          if (!crmShift) {
            crmShift = (await api.post(`/shifts/open/${storeId}`).catch(() => ({ data: null }))).data;
          }
          setShiftOpen(true);
        } else {
          // ПРРО недоступний (немає пароля/звʼязку) → працюємо як раніше, по CRM-зміні
          setShiftOpen(!!crmShift);
          if (!crmShift) return;
        }
      } catch (e) {
        setShiftOpen(!!crmShift);
        if (!crmShift) return;
      }
    } else {
      setShiftOpen(!!crmShift);
      if (!crmShift) return;
    }
    const [productsRes, catsRes, storesRes, salesRes] = await Promise.all([
      api.get(`/pos/products/${storeId}`).catch(() => ({ data: [] })),
      api.get(`/catalog/stores/${storeId}/categories`).catch(() => ({ data: [] })),
      api.get('/stores/').catch(() => ({ data: [] })),
      api.get(`/pos/sales/${storeId}`).catch(() => ({ data: { sales_count:0, total:'0.00' } })),
    ]);
    setProducts(productsRes.data);
    setCategories(catsRes.data);
    setTodaySales(salesRes.data);
    api.get(`/promotions/stores/${storeId}/active`)
      .then(r => { setActivePromos(r.data); storeSettingsRef._promos = r.data; })
      .catch(() => {});
    const store = storesRes.data.find(s => s.id === storeId);
    setStoreName(store?.name || '');
    setStoreInfo(store || null);
    if (store) {
      let capProduct = null, bagProduct = null;
      if (store.cap_enabled && store.cap_product_id) {
        capProduct = productsRes.data.find(p => p.id === store.cap_product_id) || null;
        if (!capProduct) {
          const r = await api.get(`/containers/stores/${storeId}`).catch(() => ({data:[]}));
          capProduct = r.data.find(p => p.id === store.cap_product_id) || null;
        }
      }
      if (store.bag_enabled && store.bag_product_id) {
        bagProduct = productsRes.data.find(p => p.id === store.bag_product_id) || null;
        if (!bagProduct) {
          const r = await api.get(`/containers/stores/${storeId}`).catch(() => ({data:[]}));
          bagProduct = r.data.find(p => p.id === store.bag_product_id) || null;
        }
      }
      storeSettingsRef.current = {
        cap_enabled: store.cap_enabled === true, cap_product: capProduct,
        bag_enabled: store.bag_enabled === true, bag_product: bagProduct,
      };
    }
  }

  // ── Сканер штрих-кодів (працює як клавіатура: швидко «набирає» код + Enter) ──
  const scanRef = useRef({ buf: '', last: 0 });
  const [scanClear, setScanClear] = useState(0);  // лічильник: інкремент → ProductGrid чистить пошук
  useEffect(() => {
    function onKey(e) {
      if (!shiftOpen) return;
      const now = Date.now();
      const st = scanRef.current;
      if (now - st.last > 120) st.buf = '';   // велика пауза → людина друкує, скидаємо буфер
      st.last = now;
      if (e.key === 'Enter') {
        const code = st.buf.trim();
        st.buf = '';
        if (code.length >= 3) {
          const prod = products.find(p => p.barcode && String(p.barcode).trim() === code);
          if (prod) {
            e.preventDefault();
            if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
            addToCart(prod);   // штучний → одразу в кошик; ваговий/розлив → відкриє вибір як завжди
            setScanClear(v => v + 1);  // сигнал очистити поле пошуку (щоб не заважало наступному)
          }
        }
        return;
      }
      if (e.key && e.key.length === 1 && /[0-9A-Za-z]/.test(e.key)) st.buf += e.key;
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, shiftOpen]);

  async function addToCart(product) {
    // Ваговий товар на десктоп-касі → одразу додаємо з поточною вагою (як у 1С):
    // спочатку кладуть товар на ваги, потім тиснуть позицію.
    if (product.unit === 'gram' && isDesktop) {
      const w = liveWeight && liveWeight.weight != null ? liveWeight.weight : 0;
      if (w <= 0) { setError(`Постав «${product.name}» на ваги — вага нульова`); return; }
      addWeighed(product, w);
      return;
    }
    if (product.unit === 'liter' || product.unit === 'gram') {
      try {
        const res = await api.get(`/containers/pos/${storeId}/product/${product.id}`);
        if (res.data && res.data.length > 0) { setVolumeModal(product); return; }
      } catch(e) {}
    }
    addToCartDirect(product, null, null);
  }

  // Додати ваговий товар із виміряною вагою (кг). Додає товар + пакет (якщо ввімкнено).
  function addWeighed(product, weightKg) {
    const qty = Math.round((parseFloat(weightKg) || 0) * 1000) / 1000;
    if (qty <= 0) { setError('Вага нульова — постав товар на ваги'); return; }
    if (qty > parseFloat(product.stock_quantity)) { setError(`Недостатньо '${product.name}' на складі`); return; }
    const s = storeSettingsRef.current;
    setError('');
    setCart(prev => {
      const items = [...prev, {
        cartKey: `${product.id}_${Date.now()}`,
        product_id: product.id, product_name: product.name,
        price: product.price, unit: product.unit, quantity: qty,
        stock_quantity: parseFloat(product.stock_quantity), is_free: false,
        category_id: products.find(p => p.id === product.id)?.category_id || null,
      }];
      if (s.bag_enabled && s.bag_product) {
        const bp = s.bag_product;
        items.push({
          cartKey: `bag_${Date.now()}_${Math.random()}`,
          product_id: bp.id, product_name: '🛍 ' + bp.name,
          price: String(bp.price), unit: 'piece', quantity: 1,
          stock_quantity: parseFloat(bp.stock_quantity) || 99999, is_container_line: true,
        });
      }
      return items;
    });
    printItemLabel(product, qty, 'кг');
  }

  // Друк наклейки на тару/порцію (десктоп-каса з принтером)
  function printItemLabel(product, qty, unitLabel) {
    if (!isDesktop || !window.hardware.printLabel) return;
    const vol = (Math.round((parseFloat(qty) || 0) * 1000) / 1000).toFixed(3).replace('.', ',');
    const address = (storeInfo && storeInfo.address) || '';
    const logo = (storeInfo && storeInfo.logo) || '';
    try { window.hardware.printLabel(buildLabelHtml(product.name, `${vol} ${unitLabel}`, address, logo)).catch(() => {}); } catch (e) {}
  }

  function addToCartDirect(product, volume, containerOption) {
    const step = volume || (product.unit === 'piece' ? 1 : product.unit === 'gram' ? 0.1 : 0.5);
    const s = storeSettingsRef.current;

    setCart(prev => {
      const newItems = [...prev];
      if (!volume) {
        const existing = prev.find(i => i.product_id === product.id && !i.is_container_line && !i.is_free);
        if (existing) {
          const newQty = Math.round((existing.quantity + step) * 1000) / 1000;
          if (newQty > parseFloat(product.stock_quantity)) return prev;
          return prev.map(i => i.cartKey === existing.cartKey ? {...i, quantity: newQty} : i);
        }
      }

      newItems.push({
        cartKey: `${product.id}_${Date.now()}`,
        product_id: product.id,
        product_name: product.name + (volume ? ' ' + volume + 'л' : ''),
        price: product.price,
        unit: product.unit,
        quantity: step,
        stock_quantity: parseFloat(product.stock_quantity),
        is_free: false,
        category_id: products.find(pr => pr.id === product.id)?.category_id || null,
      });

      // Тара
      if (containerOption) {
        newItems.push({
          cartKey: `container_${containerOption.container_id}_${Date.now()}`,
          product_id: containerOption.container_id,
          product_name: containerOption.container_name,
          price: String(parseFloat(containerOption.container_price) || 0),
          unit: 'piece', quantity: 1,
          stock_quantity: parseFloat(containerOption.container_stock) || 0,
          is_container_line: true,
        });
      }

      // Кришка
      if (product.unit === 'liter' && s.cap_enabled && s.cap_product) {
        const cp = s.cap_product;
        newItems.push({
          cartKey: `cap_${Date.now()}_${Math.random()}`,
          product_id: cp.id, product_name: '🔩 ' + cp.name,
          price: String(cp.price), unit: 'piece', quantity: 1,
          stock_quantity: parseFloat(cp.stock_quantity) || 99999,
          is_container_line: true,
        });
      }

      // Пакет
      if (product.unit === 'gram' && s.bag_enabled && s.bag_product) {
        const bp = s.bag_product;
        newItems.push({
          cartKey: `bag_${Date.now()}_${Math.random()}`,
          product_id: bp.id, product_name: '🛍 ' + bp.name,
          price: String(bp.price), unit: 'piece', quantity: 1,
          stock_quantity: parseFloat(bp.stock_quantity) || 99999,
          is_container_line: true,
        });
      }

      return newItems;
    });
  }

  function addDuplicateToCart(product) {
    const step = product.unit === 'piece' ? 1 : 0.5;
    const alreadyInCart = cart.filter(i => i.product_id === product.id).reduce((s, i) => s + i.quantity, 0);
    if (alreadyInCart + step > parseFloat(product.stock_quantity)) return;
    setCart(prev => [...prev, {
      cartKey: `${product.id}_${Date.now()}`,
      product_id: product.id, product_name: product.name,
      price: product.price, unit: product.unit, quantity: step,
      stock_quantity: parseFloat(product.stock_quantity) - alreadyInCart,
    }]);
  }

  function handleSaleClick() {
    if (cart.length === 0) return;
    setShowPaymentModal(true);
  }

  function handleVolumeSelect(option) {
    const prod = volumeModal;
    addToCartDirect(prod, parseFloat(option.volume), option.container);
    printItemLabel(prod, parseFloat(option.volume), 'л');
    setVolumeModal(null);
  }

  function updateCartItem(cartKey, qty) {
    setCart(prev => prev.map(i => i.cartKey === cartKey ? {...i, quantity: qty} : i));
  }

  function removeFromCart(cartKey) {
    setCart(prev => prev.filter(i => i.cartKey !== cartKey));
  }

  function clearCart() {
    updateCheck(activeId, { cart:[], customer:null, bonusToUse:'0', paymentMethod:'cash' });
    setError('');
  }

  async function completeSale(paymentMeta) {
    const bonusDiscount = parseFloat(bonusToUse) || 0;
    setLoading(true); setError('');
    try {
      const res = await api.post('/pos/sale', {
        store_id: storeId,
        customer_phone: customer?.phone || null,
        items: cart.map(i => ({
          product_id: i.product_id,
          quantity: String(Math.round(i.quantity * 1000) / 1000),
        })),
        payment_method: paymentMethod,
        bonus_to_use: bonusDiscount > 0 ? String(bonusDiscount) : '0',
        skip_promotions: noPromo,
      });
      setReceiptMeta(paymentMeta);
      setShowPaymentModal(false);
      setReceipt(res.data);
      // Друк звичайного товарного чека (десктоп). У вебі лишається ReceiptModal з кнопкою друку.
      const printOwnReceipt = () => {
        if (!(isDesktop && window.hardware.printReceipt)) return;
        try {
          window.hardware
            .printReceipt(buildReceiptHtml(res.data, storeInfo, storeName, paymentMeta))
            .catch(() => {});
        } catch (e) {}
      };
      // Фіскалізація через ПРРО iNNOVATE (десктоп-каса). Тіло чека будуємо ТУТ (у вебі),
      // і шлемо в ПРРО через realize — так правки фіскалки не вимагають перевстановлення каси.
      // НЕ блокує продаж: якщо точка ще на 1С (writeEnabled=false) — друкуємо звичайний чек.
      if (isDesktop && window.hardware.prro && window.hardware.prro.realize) {
        const prroBody = buildRealizeBody(res.data, {
          cashierName: user?.full_name || user?.name || user?.email || 'Касир',
          card: paymentMeta?.card_auth || null,
          cashGiven: paymentMeta?.cash_given,
        });
        window.hardware.prro.realize(prroBody).then(async fr => {
          if (fr && fr.ok && fr.data) {
            // Успіх → друкуємо ОФІЦІЙНИЙ фіскальний чек ПРРО (з QR ДПС).
            // QR генеруємо ЛОКАЛЬНО (data-URL), щоб друк не чекав зовнішню картинку.
            let qrDataUrl = null;
            try {
              if (fr.data.QR) qrDataUrl = await QRCode.toDataURL(String(fr.data.QR), { margin: 1, width: 200 });
            } catch (e) {}
            try { window.hardware.printReceipt(buildFiscalReceiptHtml(fr.data, qrDataUrl)).catch(() => {}); } catch (e) {}
          } else {
            const msg = (fr && fr.error) || 'невідома помилка';
            console.warn('ПРРО не фіскалізував:', msg);
            try { window.alert('🧾 ПРРО НЕ фіскалізував чек:\n\n' + msg + '\n\nНадруковано звичайний товарний чек. Перешліть це розробнику.'); } catch (e) {}
            printOwnReceipt();
          }
        }).catch(e => { try { window.alert('🧾 ПРРО помилка: ' + e.message); } catch (_) {} printOwnReceipt(); });
      } else {
        printOwnReceipt();
      }
      const [productsRes, salesRes] = await Promise.all([
        api.get(`/pos/products/${storeId}`),
        api.get(`/pos/sales/${storeId}`),
      ]);
      setProducts(productsRes.data);
      setTodaySales(salesRes.data);
      removeCheck(activeId);
    } catch(err) {
      setError(err.response?.data?.detail || 'Помилка при продажу');
    } finally { setLoading(false); }
  }

  // якщо акції вимкнені для чека (напр. доставка) — не застосовуємо їх
  const split = noPromo ? { cart, moneyDiscount: 0, lines: [] } : splitPromos(cart);
  const displayCart = split.cart;
  const subtotal = displayCart.reduce((s, i) => s + parseFloat(i.price) * i.quantity, 0);
  const totalAfterPromos = Math.round((subtotal - split.moneyDiscount) * 100) / 100;
  const bonusDiscount = Math.min(parseFloat(bonusToUse) || 0, customer ? parseFloat(customer.bonus_balance) : 0, totalAfterPromos * maxSpendFrac);

  if (shiftOpen === null) return (
    <div style={styles.container}>
      <div style={styles.center}><p style={{color:'#9a8a6e'}}>Завантаження...</p></div>
    </div>
  );

  if (!shiftOpen) return (
    <div style={styles.container}>
      <header style={styles.header}>
        <button style={styles.exitBtn} onClick={() => navigate(`/store/${storeId}`)}>← Вийти</button>
        <div style={styles.brand}>
          <span style={styles.brandMug}>🍺</span>
          <span style={styles.brandTxt}>Draft<b style={{color:'#f5a623'}}>Beer</b></span>
        </div>
        <span style={styles.headDivider}>/</span>
        <span style={styles.logo}>💰 POS Каса</span>
      </header>
      <div style={styles.center}>
        <div style={{fontSize:'64px', marginBottom:'16px'}}>🔒</div>
        <h2 style={{color:'#f5a623', marginBottom:'8px'}}>Зміна не відкрита</h2>
        <p style={{color:'#9a8a6e', marginBottom:'24px'}}>Щоб почати продажі — відкрийте зміну</p>
        <button style={styles.openShiftBtn} onClick={() => navigate(`/store/${storeId}`)}>Відкрити зміну →</button>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <button style={styles.exitBtn} onClick={() => {
          const hasItems = checks.some(c => c.cart.length > 0);
          if (hasItems) {
            if (window.confirm('Вийти з каси? Всі незакриті чеки будуть скасовані.')) navigate(`/store/${storeId}`);
          } else navigate(`/store/${storeId}`);
        }}>← Вийти</button>
        <div style={styles.brand}>
          <span style={styles.brandMug}>🍺</span>
          <span style={styles.brandTxt}>Draft<b style={{color:'#f5a623'}}>Beer</b></span>
        </div>
        <span style={styles.headDivider}>/</span>
        <span style={styles.logo}>💰 {storeName}</span>
        <div style={{flex:1}} />
        {prroShift && (
          <span style={styles.prroChip}
            title={`Реальна зміна ПРРО iNNOVATE${prroShift.shiftOpenDate ? ' · з ' + prroShift.shiftOpenDate : ''}${prroShift.cashierName ? ' · ' + prroShift.cashierName : ''} · чек №${prroShift.lastReceiptNumber ?? '—'}`}>
            🧾 ПРРО: {prroShift.shiftOpen ? 'зміна відкрита' : 'зміна закрита'}
          </span>
        )}
        {isDesktop && liveWeight && liveWeight.weight != null && (
          <span style={styles.weighChip} title="Живий вес з ваг">
            ⚖️ {liveWeight.weight.toFixed(3)} кг
          </span>
        )}
        <ShiftStats stats={todaySales} />
      </header>

      <div style={styles.checksBar}>
        {checks.map((check, i) => {
          const isActive = check.id === activeId;
          const itemCount = check.cart.filter(item => !item.is_container_line).length;
          const total = check.cart.reduce((s, item) => s + parseFloat(item.price) * item.quantity, 0);
          return (
            <div key={check.id}
              style={{...styles.checkTab, ...(isActive ? styles.checkTabActive : {})}}
              onClick={() => setActiveCheckId(check.id)}>
              <span style={styles.checkTabNum}>🧾 Чек {i+1}</span>
              {itemCount > 0
                ? <span style={styles.checkTabInfo}>{itemCount} поз · {total.toFixed(0)} ₴</span>
                : <span style={styles.checkTabEmpty}>порожній</span>}
              {checks.length > 1 && (
                <button style={styles.checkTabClose}
                  onClick={e => { e.stopPropagation(); removeCheck(check.id); }}>✕</button>
              )}
            </div>
          );
        })}
        <button style={styles.addCheckBtn} onClick={addNewCheck}>+ Новий чек</button>
      </div>

      <div className="pos-layout">
        <ProductGrid
          products={products} categories={categories} cart={displayCart}
          onAdd={addToCart} onAddDuplicate={addDuplicateToCart}
          activePromos={activePromos} scanClear={scanClear}
        />
        <div className="pos-cart">
          <CustomerSearch
            key={activeId}
            customer={customer} onSelect={setCustomer}
            onClear={() => { setCustomer(null); setBonusToUse('0'); }}
            onBonusChange={setBonusToUse} subtotal={subtotal}
            maxSpendFrac={maxSpendFrac}
          />
          <button
            style={{...styles.promoToggle, ...(noPromo ? styles.promoToggleOff : {})}}
            onClick={() => setNoPromo(!noPromo)}
            title="Напр. для доставки — щоб акції не діяли в цьому чеку">
            {noPromo ? '🚫 Акції вимкнені (натисни щоб увімкнути)' : '🎁 Акції увімкнені (натисни щоб вимкнути)'}
          </button>
          <Cart
            cart={displayCart} customer={customer} bonusToUse={bonusToUse}
            paymentMethod={paymentMethod} onPaymentChange={setPaymentMethod}
            onUpdate={updateCartItem} onRemove={removeFromCart}
            onClear={clearCart} onSale={handleSaleClick}
            loading={loading} error={error}
            promoDiscounts={split.lines} totalAfterPromos={totalAfterPromos}
            earnFrac={earnFrac} maxSpendFrac={maxSpendFrac}
          />
        </div>
      </div>

      {showPaymentModal && (
        <PaymentModal total={totalAfterPromos - bonusDiscount}
          paymentMethod={paymentMethod} onConfirm={completeSale}
          onClose={() => setShowPaymentModal(false)} />
      )}

      {receipt && !isDesktop && (
        <ReceiptModal receipt={receipt} storeName={storeName} store={storeInfo}
          cashGiven={receiptMeta.cash_given} change={receiptMeta.change}
          onClose={() => { setReceipt(null); setShowPaymentModal(false); }} />
      )}

      {volumeModal && (
        <VolumeModal product={volumeModal} storeId={storeId}
          onSelect={handleVolumeSelect} onClose={() => setVolumeModal(null)} />
      )}

    </div>
  );
}

const styles = {
  container: { height:'100vh', background:'#14110c', color:'#fff', display:'flex', flexDirection:'column', overflow:'hidden' },
  checksBar: { background:'#211c14', borderBottom:'2px solid #38301f', display:'flex', alignItems:'stretch', padding:'0 12px', gap:'6px', flexShrink:0, overflowX:'auto' },
  checkTab: { display:'flex', alignItems:'center', gap:'8px', padding:'10px 16px', cursor:'pointer', borderBottom:'3px solid transparent', whiteSpace:'nowrap', marginBottom:'-2px' },
  checkTabActive: { borderBottom:'3px solid #f5a623', background:'#1c1812', borderRadius:'8px 8px 0 0' },
  checkTabNum: { fontSize:'14px', fontWeight:'bold', color:'#fff' },
  checkTabInfo: { fontSize:'12px', color:'#f5a623', background:'#38301f', padding:'2px 8px', borderRadius:'10px', fontWeight:'bold' },
  checkTabEmpty: { fontSize:'11px', color:'#9a8a6e' },
  checkTabClose: { background:'transparent', border:'none', color:'#9a8a6e', cursor:'pointer', fontSize:'15px', padding:'0 2px', lineHeight:1, marginLeft:'2px' },
  addCheckBtn: { margin:'8px 0 8px 4px', padding:'8px 16px', background:'transparent', color:'#5fbf8f', border:'1px dashed #2d6a4f', borderRadius:'8px', cursor:'pointer', fontSize:'13px', whiteSpace:'nowrap', flexShrink:0, alignSelf:'center' },
  weighChip: { display:'flex', alignItems:'center', gap:'6px', background:'#211c14', border:'1px solid #38301f', borderRadius:'10px', padding:'6px 12px', fontSize:'14px', fontWeight:700, whiteSpace:'nowrap' },
  prroChip: { display:'flex', alignItems:'center', gap:'6px', background:'rgba(95,191,143,.12)', border:'1px solid #2f6f52', color:'#5fbf8f', borderRadius:'10px', padding:'6px 12px', fontSize:'13px', fontWeight:700, whiteSpace:'nowrap', marginRight:'8px' },
  promoToggle: { flexShrink:0, margin:'0 12px 8px', padding:'8px 10px', borderRadius:'8px', border:'1px solid #2d6a4f', background:'rgba(95,191,143,.10)', color:'#5fbf8f', fontSize:'12.5px', fontWeight:700, cursor:'pointer' },
  promoToggleOff: { border:'1px solid rgba(255,107,107,.4)', background:'rgba(255,107,107,.12)', color:'#ff6b6b' },
  header: { background:'rgba(20,17,12,.92)', padding:'12px clamp(12px,3vw,24px)', display:'flex', alignItems:'center', gap:'12px', borderBottom:'1px solid #38301f', flexShrink:0, flexWrap:'wrap' },
  exitBtn: { background:'#2a2419', color:'#f4ead7', border:'1px solid #38301f', borderRadius:'10px', padding:'8px 14px', fontSize:'13px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  brand: { display:'flex', alignItems:'center', gap:'9px' },
  brandMug: { fontSize:'22px', filter:'drop-shadow(0 3px 8px rgba(245,166,35,.4))' },
  brandTxt: { fontFamily:"'Unbounded', sans-serif", fontWeight:800, fontSize:'18px', color:'#f4ead7' },
  headDivider: { color:'#9a8a6e' },
  logo: { color:'#c9b896', margin:0, fontSize:'16px', fontFamily:"'Unbounded', sans-serif", fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  center: { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' },
  openShiftBtn: { padding:'12px 32px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'10px', fontSize:'15px', fontWeight:'bold', cursor:'pointer' },
};
